import { ESignDocument } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useEffect, useRef } from "react";

import type { SummaryOfRightsEsignData } from "../types";

type SummaryOfRightsEsignProps = {
  data: SummaryOfRightsEsignData;
};

const CONTENT_SELECTOR = ".ProseMirror, .super-editor__element, .super-editor";
const VIEWER_SELECTOR = ".superdoc-esign-document-viewer";

/**
 * Read-only SuperDoc HTML in the report card, matching PactSafe ContractView.
 * SuperDoc's default editor is a nested scroller; we size it to the document
 * so the report page scrolls instead.
 */
export function SummaryOfRightsEsign({ data }: SummaryOfRightsEsignProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;

    const expandToContent = () => {
      const content = root.querySelector(CONTENT_SELECTOR);
      const viewer = root.querySelector(VIEWER_SELECTOR);
      if (!(content instanceof HTMLElement)) return;

      const height = Math.max(content.scrollHeight, content.offsetHeight);
      if (height <= 0) return;

      if (viewer instanceof HTMLElement) {
        viewer.style.height = `${height}px`;
        viewer.style.maxHeight = "none";
        viewer.style.overflow = "visible";
      }
    };

    expandToContent();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(expandToContent);
    const mutationObserver = new MutationObserver(() => {
      const content = root.querySelector(CONTENT_SELECTOR);
      if (content && resizeObserver) {
        resizeObserver.observe(content);
      }
      expandToContent();
    });

    mutationObserver.observe(root, { childList: true, subtree: true });
    const existing = root.querySelector(CONTENT_SELECTOR);
    if (existing && resizeObserver) {
      resizeObserver.observe(existing);
    }

    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [data.url]);

  return (
    <div
      ref={wrapperRef}
      className={styles.wrapper}
      data-testid="summary-of-rights-esign"
    >
      <ESignDocument
        data={data}
        layoutMode="responsive"
        allowExpand={false}
        style={{ height: "auto" }}
      />
    </div>
  );
}

const SUPERDOC_CONTENT_SELECTORS = [
  "& > div",
  "& section",
  "& .superdoc-esign-container",
  "& .superdoc-esign-document",
  "& .superdoc-esign-document-viewer",
  "& .superdoc",
  "& .superdoc__layers",
  "& .superdoc__document",
  "& .superdoc__sub-document",
  "& .super-editor",
  "& .super-editor-container",
  "& .super-editor__element",
  "& .ProseMirror",
].join(", ");

const styles = {
  wrapper: css({
    width: "100%",
    height: "auto",
    "& .superdoc-esign-document-toolbar": {
      display: "none",
    },
    "& .superdoc-esign-controls": {
      display: "none",
    },
    [SUPERDOC_CONTENT_SELECTORS]: {
      height: "auto !important",
      minHeight: "0 !important",
      maxHeight: "none !important",
      overflow: "visible !important",
      flex: "none !important",
    },
  }),
};
